import type {
  Acknowledgement,
  EventName,
  LetterStatus,
} from "../../shared/contracts";
import {
  collectEventRequestSchema,
  createLetterRequestSchema,
} from "../../shared/contracts";
import { sha256Hex } from "../lib/encoding";
import { database } from "../storage/database";
import type { DemoPackageRecord } from "../types";

type BackendMode = "demo" | "supabase";

const isPagesDiagnosticHost = window.location.hostname.endsWith(".pages.dev");
const backendMode: BackendMode =
  import.meta.env.VITE_BACKEND_MODE === "supabase" && !isPagesDiagnosticHost
    ? "supabase"
    : "demo";
const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(
  /\/$/,
  "",
);
const publishableKey = String(
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
);

interface CreateLetterInput {
  letterId: string;
  encryptedPackage: Blob;
  expiresInDays: 7 | 30 | 90;
  creatorToken: string;
  readerToken: string;
  replyToLetterId: string | null;
  replyReaderToken: string | null;
  challengeToken?: string | null;
}

interface LetterAccess {
  status: LetterStatus;
  encryptedPackage?: Blob;
  expiresAt?: string;
}

interface CreatorStatus {
  status: LetterStatus;
  expiresAt?: string;
  openedAt?: string | null;
  openCount?: number;
  acknowledgement?: Acknowledgement | null;
}

function assertRemoteConfiguration(): void {
  if (!supabaseUrl || !publishableKey) {
    throw new Error(
      "Supabase mode is enabled, but the public project URL or publishable key is missing.",
    );
  }
}

export class BackendError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function invoke<T>(
  functionName: "letters" | "events",
  action: string,
  body: unknown,
): Promise<T> {
  assertRemoteConfiguration();
  const response = await fetch(
    `${supabaseUrl}/functions/v1/${functionName}/${action}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: publishableKey,
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify(body),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  if (!response.ok) {
    throw new BackendError(
      payload.code ?? "service_error",
      payload.error ?? `Fold service request failed (${response.status}).`,
      response.status,
    );
  }
  return payload as T;
}

export function getBackendMode(): BackendMode {
  return backendMode;
}

export async function createAndUploadLetter(
  input: CreateLetterInput,
): Promise<{ expiresAt: string }> {
  const creatorTokenHash = await sha256Hex(input.creatorToken);
  const readerTokenHash = await sha256Hex(input.readerToken);
  const expiresAt = new Date(
    Date.now() + input.expiresInDays * 86_400_000,
  ).toISOString();
  if (backendMode === "demo") {
    const record: DemoPackageRecord = {
      letterId: input.letterId,
      encryptedPackage: input.encryptedPackage,
      status: "active",
      expiresAt,
      creatorTokenHash,
      readerTokenHash,
      openedAt: null,
      openCount: 0,
      acknowledgement: null,
    };
    await database.demoPackages.put(record);
    return { expiresAt };
  }

  const request = createLetterRequestSchema.parse({
    letterId: input.letterId,
    packageVersion: 1,
    expectedSizeBytes: input.encryptedPackage.size,
    expiresInDays: input.expiresInDays,
    creatorTokenHash,
    readerTokenHash,
    replyToLetterId: input.replyToLetterId,
    replyReaderToken: input.replyReaderToken,
    challengeToken: input.challengeToken ?? null,
  });
  const authorization = await invoke<{
    path: string;
    token: string;
    expiresAt: string;
  }>("letters", "create", request);
  const { createClient } = await import("@supabase/supabase-js");
  const client = createClient(supabaseUrl, publishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });
  const { error: uploadError } = await client.storage
    .from("letters")
    .uploadToSignedUrl(
      authorization.path,
      authorization.token,
      input.encryptedPackage,
      {
        contentType: "application/octet-stream",
        upsert: false,
      },
    );
  if (uploadError)
    throw new Error(
      "The encrypted letter could not be uploaded. Your draft is still safe.",
    );
  await invoke("letters", "complete", {
    letterId: input.letterId,
    creatorToken: input.creatorToken,
  });
  return { expiresAt: authorization.expiresAt };
}

export async function accessLetter(letterId: string): Promise<LetterAccess> {
  if (backendMode === "demo") {
    const record = await database.demoPackages.get(letterId);
    if (!record) return { status: "not_found" };
    if (
      record.status === "active" &&
      new Date(record.expiresAt).getTime() <= Date.now()
    ) {
      await database.demoPackages.update(letterId, { status: "expired" });
      return { status: "expired", expiresAt: record.expiresAt };
    }
    if (record.status !== "active")
      return { status: record.status, expiresAt: record.expiresAt };
    return {
      status: "active",
      encryptedPackage: record.encryptedPackage,
      expiresAt: record.expiresAt,
    };
  }
  const access = await invoke<{
    status: LetterStatus;
    downloadUrl?: string;
    expiresAt?: string;
  }>("letters", "access", { letterId });
  if (access.status !== "active" || !access.downloadUrl) return access;
  const response = await fetch(access.downloadUrl, {
    cache: "no-store",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok)
    throw new Error("The encrypted letter could not be downloaded.");
  return { ...access, encryptedPackage: await response.blob() };
}

export async function recordOpen(
  letterId: string,
  readerToken: string,
): Promise<void> {
  if (backendMode === "demo") {
    const record = await database.demoPackages.get(letterId);
    if (!record || record.readerTokenHash !== (await sha256Hex(readerToken)))
      return;
    await database.demoPackages.update(letterId, {
      openedAt: record.openedAt ?? new Date().toISOString(),
      openCount: record.openCount + 1,
    });
    return;
  }
  await collectEvent("letter_opened", { letterId, readerToken });
}

export async function acknowledgeLetter(
  letterId: string,
  readerToken: string,
  acknowledgement: Acknowledgement,
): Promise<void> {
  if (backendMode === "demo") {
    const record = await database.demoPackages.get(letterId);
    if (!record || record.readerTokenHash !== (await sha256Hex(readerToken))) {
      throw new Error("This letter cannot be acknowledged from this link.");
    }
    await database.demoPackages.update(letterId, { acknowledgement });
    return;
  }
  await invoke("letters", "acknowledge", {
    letterId,
    readerToken,
    acknowledgement,
  });
}

export async function getCreatorStatus(
  letterId: string,
  creatorToken: string,
): Promise<CreatorStatus> {
  if (backendMode === "demo") {
    const record = await database.demoPackages.get(letterId);
    if (!record || record.creatorTokenHash !== (await sha256Hex(creatorToken)))
      return { status: "not_found" };
    return {
      status: record.status,
      expiresAt: record.expiresAt,
      openedAt: record.openedAt,
      openCount: record.openCount,
      acknowledgement: record.acknowledgement,
    };
  }
  return invoke("letters", "creator-status", { letterId, creatorToken });
}

export async function revokeLetter(
  letterId: string,
  creatorToken: string,
): Promise<void> {
  if (backendMode === "demo") {
    const record = await database.demoPackages.get(letterId);
    if (
      !record ||
      record.creatorTokenHash !== (await sha256Hex(creatorToken))
    ) {
      throw new Error(
        "This device no longer has permission to revoke the letter.",
      );
    }
    await database.demoPackages.update(letterId, { status: "revoked" });
    return;
  }
  await invoke("letters", "revoke", { letterId, creatorToken });
}

export async function collectEvent(
  eventName: EventName,
  options: {
    letterId?: string;
    draftSessionId?: string;
    readerToken?: string;
    properties?: Record<string, string | number | boolean>;
  } = {},
): Promise<void> {
  if (backendMode === "demo") return;
  const payload = collectEventRequestSchema.parse({
    eventId: crypto.randomUUID(),
    eventName,
    letterId: options.letterId ?? null,
    draftSessionId: options.draftSessionId ?? null,
    readerToken: options.readerToken ?? null,
    schemaVersion: 1,
    properties: options.properties ?? {},
  });
  await invoke("events", "collect", payload);
}
